import type { ChangeEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import SearchIcon from "@mui/icons-material/Search";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import {
  FINANCEIRO_ACCENT,
  financeiroToolbarSx,
} from "../../components/financeiro-lancamentos.theme";
import type {
  ContaFinanceiraFiltroApi,
  ExtratoFinanceiroFiltros as ExtratoFiltrosState,
} from "../types/extrato-financeiro.types";
import {
  CODIGO_CONTA_CAIXA,
  CODIGO_CONTA_GATEWAY_EFI,
} from "../types/contas-financeiras.types";
import {
  periodoExtratoParaDatas,
  type ExtratoPeriodoPreset,
} from "../helpers/extrato-financeiro-datas";

function labelContaFiltroExtrato(conta: ContaFinanceiraFiltroApi): string {
  if (conta.codigoInterno === CODIGO_CONTA_GATEWAY_EFI || conta.tipo === "GATEWAY") {
    return "Efí";
  }
  if (conta.codigoInterno === CODIGO_CONTA_CAIXA || conta.tipo === "CAIXA") {
    return "Caixa";
  }
  return conta.nome;
}

function ordenarContasFiltroExtrato(
  contas: ContaFinanceiraFiltroApi[],
): ContaFinanceiraFiltroApi[] {
  const rank = (c: ContaFinanceiraFiltroApi) => {
    if (c.codigoInterno === CODIGO_CONTA_GATEWAY_EFI || c.tipo === "GATEWAY") return 0;
    if (c.codigoInterno === CODIGO_CONTA_CAIXA || c.tipo === "CAIXA") return 1;
    return 2;
  };
  return [...contas].sort((a, b) => {
    const diff = rank(a) - rank(b);
    if (diff !== 0) return diff;
    return a.nome.localeCompare(b.nome, "pt-BR");
  });
}

type OpcaoStr = { value: string; label: string };

const OPCOES_PERIODO: { value: ExtratoPeriodoPreset; label: string }[] = [
  { value: "hoje", label: "Hoje" },
  { value: "7d", label: "7 dias" },
  { value: "30d", label: "30 dias" },
  { value: "mes", label: "Este mês" },
];

const OPCOES_TIPO: OpcaoStr[] = [
  { value: "", label: "Todos" },
  { value: "ENTRADA", label: "Entrada" },
  { value: "SAIDA", label: "Saída" },
  { value: "TRANSFERENCIA", label: "Transferência" },
  { value: "TAXA", label: "Taxa" },
  { value: "AJUSTE", label: "Ajuste" },
  { value: "ESTORNO", label: "Estorno" },
];

const OPCOES_ORIGEM: OpcaoStr[] = [
  { value: "", label: "Todas" },
  { value: "WEBHOOK_EFI", label: "Webhook EFI" },
  { value: "MANUAL", label: "Manual" },
  { value: "AJUSTE_SISTEMA", label: "Ajuste sistema" },
  { value: "IMPORTACAO", label: "Importação" },
];

const toggleSx = {
  flexWrap: "wrap" as const,
  "& .MuiToggleButton-root": {
    px: 1.25,
    py: 0.75,
    textTransform: "none" as const,
    fontSize: "0.8125rem",
    "&.Mui-selected": {
      bgcolor: "rgba(249, 96, 48, 0.1)",
      borderColor: FINANCEIRO_ACCENT,
      color: FINANCEIRO_ACCENT,
      fontWeight: 600,
    },
  },
};

const fieldSx = { bgcolor: "background.paper", borderRadius: 1.5 };

export type ExtratoFinanceiroFiltrosProps = {
  filtros: ExtratoFiltrosState;
  setFiltros: (
    patch:
      | Partial<ExtratoFiltrosState>
      | ((p: ExtratoFiltrosState) => ExtratoFiltrosState),
  ) => void;
  onAplicar: () => void;
  onLimpar: () => void;
  loading?: boolean;
  contasFinanceiras: ContaFinanceiraFiltroApi[];
  contasFinanceirasIndisponivel?: boolean;
  resetPeriodoPresetTick?: number;
};

export default function ExtratoFinanceiroFiltros({
  filtros,
  setFiltros,
  onAplicar,
  onLimpar,
  loading,
  contasFinanceiras,
  contasFinanceirasIndisponivel,
  resetPeriodoPresetTick = 0,
}: ExtratoFinanceiroFiltrosProps) {
  const [periodoPreset, setPeriodoPreset] =
    useState<ExtratoPeriodoPreset | "personalizado">("mes");

  useEffect(() => {
    setPeriodoPreset("mes");
  }, [resetPeriodoPresetTick]);

  const aplicarPeriodo = (preset: ExtratoPeriodoPreset) => {
    setPeriodoPreset(preset);
    const datas = periodoExtratoParaDatas(preset);
    if (datas) {
      setFiltros((prev) => ({
        ...prev,
        dataInicio: datas.dataInicio,
        dataFim: datas.dataFim,
      }));
    }
  };

  const patch =
    (field: keyof ExtratoFiltrosState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFiltros((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const contasRapidas = useMemo(
    () => ordenarContasFiltroExtrato(contasFinanceiras),
    [contasFinanceiras],
  );

  const contaRapidaValue = filtros.contaFinanceiraId || "todas";
  const periodoToggleValue =
    periodoPreset === "personalizado" ? "" : periodoPreset;

  return (
    <MDBox sx={financeiroToolbarSx}>
      <Stack spacing={2.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <AccountBalanceWalletOutlinedIcon sx={{ color: FINANCEIRO_ACCENT }} />
          <MDTypography variant="button" fontWeight="medium" color="text">
            Filtros operacionais
          </MDTypography>
        </Stack>

        <Stack spacing={0.75}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Conta financeira
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={contaRapidaValue}
            onChange={(_, next: string | null) => {
              if (next === null) return;
              setFiltros((prev) => ({
                ...prev,
                contaFinanceiraId: next === "todas" ? "" : next,
              }));
            }}
            sx={toggleSx}
          >
            <ToggleButton value="todas" disabled={Boolean(loading)}>
              Todas as contas
            </ToggleButton>
            {contasRapidas.map((conta) => (
              <ToggleButton
                key={conta.id}
                value={conta.id}
                disabled={Boolean(loading)}
                title={conta.nome}
              >
                {labelContaFiltroExtrato(conta)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        <Stack spacing={0.75}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Período
          </Typography>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", lg: "flex-end" }}
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              value={periodoToggleValue}
              onChange={(_, next: ExtratoPeriodoPreset | null) => {
                if (next) aplicarPeriodo(next);
              }}
              sx={{ ...toggleSx, flexShrink: 0 }}
            >
              {OPCOES_PERIODO.map((opt) => (
                <ToggleButton key={opt.value} value={opt.value} disabled={Boolean(loading)}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <FormControl variant="outlined" size="small" sx={{ minWidth: 160, flex: 1 }}>
              <InputLabel shrink htmlFor="extrato-fin-inicio">
                Data início
              </InputLabel>
              <OutlinedInput
                id="extrato-fin-inicio"
                type="date"
                value={filtros.dataInicio ?? ""}
                onChange={(e) => {
                  setPeriodoPreset("personalizado");
                  patch("dataInicio")(e);
                }}
                label="Data início"
                notched
                disabled={Boolean(loading)}
                sx={fieldSx}
              />
            </FormControl>

            <FormControl variant="outlined" size="small" sx={{ minWidth: 160, flex: 1 }}>
              <InputLabel shrink htmlFor="extrato-fin-fim">
                Data fim
              </InputLabel>
              <OutlinedInput
                id="extrato-fin-fim"
                type="date"
                value={filtros.dataFim ?? ""}
                onChange={(e) => {
                  setPeriodoPreset("personalizado");
                  patch("dataFim")(e);
                }}
                label="Data fim"
                notched
                disabled={Boolean(loading)}
                sx={fieldSx}
              />
            </FormControl>
          </Stack>
          {contasFinanceirasIndisponivel ? (
            <Typography variant="caption" color="warning.main">
              Lista de contas indisponível — recarregue a página ou tente novamente.
            </Typography>
          ) : contasRapidas.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              Nenhuma conta financeira ativa cadastrada.
            </Typography>
          ) : null}
        </Stack>

        <Stack spacing={0.75}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Tipo de movimentação
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={filtros.tipo ?? ""}
            onChange={(_, next: string | null) => {
              if (next === null) return;
              setFiltros((prev) => ({ ...prev, tipo: next }));
            }}
            sx={toggleSx}
          >
            {OPCOES_TIPO.map((opt) => (
              <ToggleButton
                key={opt.value || "all-tipo"}
                value={opt.value}
                disabled={Boolean(loading)}
              >
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        <Stack spacing={0.75}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Origem do registro
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={filtros.origem ?? ""}
            onChange={(_, next: string | null) => {
              if (next === null) return;
              setFiltros((prev) => ({ ...prev, origem: next }));
            }}
            sx={toggleSx}
          >
            {OPCOES_ORIGEM.map((opt) => (
              <ToggleButton
                key={opt.value || "all-origem"}
                value={opt.value}
                disabled={Boolean(loading)}
              >
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        <FormControl variant="outlined" size="small" fullWidth>
          <InputLabel shrink htmlFor="extrato-fin-busca">
            Busca geral
          </InputLabel>
          <OutlinedInput
            id="extrato-fin-busca"
            value={filtros.busca ?? ""}
            onChange={patch("busca")}
            label="Busca geral"
            placeholder="Descrição, observação ou IDs…"
            notched
            disabled={Boolean(loading)}
            endAdornment={
              <InputAdornment position="end">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            }
            sx={fieldSx}
          />
        </FormControl>

        <Divider />

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={!(filtros.ocultarAjusteSistema ?? true)}
                onChange={(_, checked) =>
                  setFiltros((prev) => ({
                    ...prev,
                    ocultarAjusteSistema: !checked,
                    ...(checked ? { origem: "" } : {}),
                  }))
                }
                disabled={Boolean(loading || filtros.origem === "AJUSTE_SISTEMA")}
              />
            }
            label={
              <Typography variant="body2">Mostrar ajustes de conciliação</Typography>
            }
            sx={{ m: 0 }}
          />

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">
            <MDButton
              variant="gradient"
              color="dark"
              size="small"
              disabled={Boolean(loading)}
              onClick={onAplicar}
              sx={{
                background: `linear-gradient(195deg, ${FINANCEIRO_ACCENT}, #e04a1a)`,
              }}
            >
              Aplicar filtros
            </MDButton>
            <MDButton
              color="dark"
              variant="outlined"
              size="small"
              disabled={Boolean(loading)}
              onClick={onLimpar}
            >
              Limpar
            </MDButton>
          </Stack>
        </Stack>
      </Stack>
    </MDBox>
  );
}
